import React, { useState } from 'react';
import { Link } from '../routing';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Sparkles, ArrowLeft, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AuthShell from '../components/AuthShell';
import theme from '../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  React.useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch {
      toast.error(t('forgotPassword.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>

        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', marginBottom: 32 }}>
            <Sparkles size={18} color={theme.gold} />
            <span style={{ fontFamily: theme.font.display, fontSize: 26, color: theme.ink }}>GLAM<span style={{ color: theme.gold }}>GO</span></span>
          </Link>
          <h1 style={{ fontFamily: theme.font.display, fontSize: 32, color: theme.ink, margin: '0 0 8px', fontWeight: 400 }}>{t('forgotPassword.title')}</h1>
          <p style={{ color: theme.grey400, fontSize: 14, margin: 0 }}>{t('forgotPassword.subtitle')}</p>
        </div>

        {sent ? (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle size={48} color={theme.green} style={{ marginBottom: 16 }} />
            <h3 style={{ fontFamily: theme.font.display, fontSize: 24, color: theme.ink, marginBottom: 8 }}>{t('forgotPassword.sentTitle')}</h3>
            <p style={{ color: theme.grey600, fontSize: 14, marginBottom: 32 }}>
              {t('forgotPassword.sentMessage', { email })}
            </p>
            <p style={{ color: theme.grey400, fontSize: 13 }}>{t('forgotPassword.checkSpam')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, color: theme.grey600, letterSpacing: '0.08em', marginBottom: 8 }}>{t('forgotPassword.email')}</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)} required
                placeholder={t('auth.emailPlaceholder')}
                style={{ width: '100%', padding: '14px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 15, color: theme.ink, background: theme.cream, outline: 'none', boxSizing: 'border-box', fontFamily: theme.font.body }}
                onFocus={e => e.target.style.borderColor = theme.gold}
                onBlur={e => e.target.style.borderColor = theme.sand}
              />
            </div>
            <button type="submit" disabled={loading} style={{ width: '100%', padding: '16px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 13, letterSpacing: '0.15em', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, fontFamily: theme.font.body, fontWeight: 500 }}>
              {loading ? t('forgotPassword.sending') : t('forgotPassword.send')}
            </button>
          </form>
        )}

        <p style={{ textAlign: 'center', color: theme.grey400, fontSize: 14, marginTop: 24 }}>
          <Link to="/login" style={{ color: theme.gold, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <ArrowLeft size={14} /> {t('forgotPassword.backToLogin')}
          </Link>
        </p>
    </AuthShell>
  );
}
